import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, CheckCircle2, FileX } from "lucide-react";
import { API } from "./constants";

export function ExtractionFailuresCard() {
  const [failures, setFailures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFailures = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/superadmin/extraction-failures?limit=30`, {
        withCredentials: true,
      });
      setFailures(res.data?.failures || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFailures();
  }, [fetchFailures]);

  const docLabel = (t) =>
    ({
      ine_front: "INE Frente",
      ine_back: "INE Vuelta",
      constancia_fiscal: "CSF",
    }[t] || t);

  return (
    <div
      data-testid="extraction-failures-card"
      className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-red-500/10 rounded-lg shrink-0">
            <FileX className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">
              Extracciones fallidas recientes
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Últimos 30 documentos con error de OCR. Úsalo para diagnosticar problemas
              de extracción reportados por notarías.
            </p>
          </div>
        </div>
        <button
          onClick={fetchFailures}
          data-testid="failures-refresh-btn"
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
          disabled={loading}
          title="Refrescar"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
          {error}
        </div>
      )}

      {loading && !failures ? (
        <div className="h-20 flex items-center justify-center text-slate-500 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          Cargando…
        </div>
      ) : failures && failures.length === 0 ? (
        <div
          data-testid="failures-empty-state"
          className="text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 text-center"
        >
          <CheckCircle2 className="w-5 h-5 mx-auto mb-1" />
          Sin extracciones fallidas — todo corriendo limpio.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="failures-table">
            <thead className="text-slate-400 text-left">
              <tr className="border-b border-slate-800">
                <th className="py-2 pr-3 font-medium">Cuándo</th>
                <th className="py-2 pr-3 font-medium">Doc</th>
                <th className="py-2 pr-3 font-medium">Método</th>
                <th className="py-2 pr-3 font-medium">MIME</th>
                <th className="py-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              {(failures || []).map((f, idx) => (
                <tr
                  key={`${f.draft_id}-${f.doc_type}-${idx}`}
                  className="border-b border-slate-800/60 hover:bg-slate-800/30"
                  data-testid={`failure-row-${idx}`}
                >
                  <td className="py-2 pr-3 text-slate-400 whitespace-nowrap">
                    {f.extracted_at
                      ? new Date(f.extracted_at).toLocaleString("es-MX", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })
                      : "—"}
                  </td>
                  <td className="py-2 pr-3">
                    <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300">
                      {docLabel(f.doc_type)}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-slate-500 font-mono">
                    {f.extraction_method || "—"}
                  </td>
                  <td className="py-2 pr-3 text-slate-500 font-mono">
                    {f.mime_type || "—"}
                  </td>
                  <td className="py-2 text-amber-400/90 max-w-md truncate" title={f.extraction_error || ""}>
                    {f.extraction_error || "(sin mensaje)"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
